import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import TopCards from "./TopCards";
import Clients from "./Clients";
import Vendors from "./Vendors";
import Ips from "./Ips";
import AdminList from "./AdminList";
import CampaignTable from "./CampaignTable";
import Analytics from "./Analytics";
import axios from "axios";
// import Analytics from './Analytics';
import CircularProgress from "@mui/material/CircularProgress";
import socket from "../../../shared/utils/Socket";
import Box from "@mui/material/Box";

const Dashboard = ({ loading, setLoading }) => {
  const [campaignList, setCampaignList] = useState([]);
  const [totalCampaigns, setTotalCampaign] = useState("");
  const [kamdata, setKamData] = useState([]);
  const [totalOrganizationDetail, setTotalOrganisationDetail] = useState("");
  const [isSites, SetSites] = useState([]);
  const [productAreaSum, setProductAreaSum] = useState([]);
  const [totalIps, setTotalIps] = useState("");
  const [totalVendors, setTotalVendors] = useState("");
  const [ongoingLoading, setOngoingLoading] = useState(false);
  const [page, setPage] = useState(1); // Start from page 1
  const [hasMore, setHasMore] = useState(true);
  const [initialCampaignLoading, setInitialCampaignLoading] = useState(true);

  const [TotalEarning, setTotalEarning] = useState("");
  const [lastMonthData, setLastMonthData] = useState([]);
  const [comparedCounts, setComparedCounts] = useState([]);
  const serverURL = process.env.REACT_APP_URL;
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // 1. Fetch counts & summary
  const fetchDashboardSummary = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/TotalCount/aggregateCounts`,
        { headers }
      );
      const data = res.data.data;

      setKamData(data.Kam);
      setCampaignList(data.campaigns);
      setTotalOrganisationDetail(data.totalOrganizationDetail);
      setTotalVendors(data.totalVendors);
      setTotalIps(data.totalInstallationPartners);
      setTotalEarning(data.TotalEarning);
      setLastMonthData(data.lastSixMonthsData);
    } catch (err) {
      console.error("❌ Error in fetchDashboardSummary:", err);
    }
  };

  // 2. Fetch ongoing campaigns
  const fetchOngoingCampaigns = async (page = 1, limit = 10) => {
    try {
      setOngoingLoading(true);
      if (page === 1) {
        setInitialCampaignLoading(true);
      }
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/TotalCount/getOngoingCampaign?page=${page}&limit=${limit}`,
        { headers }
      );

      const data = res.data;

      if (data.success) {
        // ✅ Append new data to existing state
        setTotalCampaign(data.totalCampaigns);

        if (page === 1) {
          // Replace existing data for first page
          setCampaignList(data.campaigns || []);
          SetSites(data.jobs || []);
          setProductAreaSum(data.campaignProductSum || []);
        } else {
          setCampaignList((prev) => [...(prev || []), ...data.campaigns]);
          SetSites((prev) => [...(prev || []), ...data.jobs]);
          setProductAreaSum((prev) => [...(prev || []), ...data.campaignProductSum]);
        }
        setHasMore(data.campaigns.length === limit);
        setPage((prev) => prev + 1);
      }
    } catch (err) {
      console.error("❌ Error in fetchOngoingCampaigns:", err);
    } finally {
      setOngoingLoading(false);
      setInitialCampaignLoading(false);
    }
  };

  // 3. Fetch compared counts
  const fetchComparedCounts = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/TotalCount/getComparedCounts`,
        { headers }
      );
      if (res.data.success) {
        setComparedCounts(res.data.data);
      }
    } catch (err) {
      console.error("❌ Error in fetchComparedCounts:", err);
    } finally {
      setLoading(false);
    }
  };

  // 4. Setup socket listeners
  const setupSocketListeners = () => {
    const handleVendor = () => setTotalVendors((prev) => Number(prev || 0) + 1);
    const handleIP = () => setTotalIps((prev) => Number(prev || 0) + 1);
    const handleClient = () =>
      setTotalOrganisationDetail((prev) => Number(prev || 0) + 1);

    socket.on("vendor-added", handleVendor);
    socket.on("new-vendor-partner", handleVendor);
    socket.on("new-installation-partner", handleIP);
    socket.on("new-client-partner", handleClient);

    // Clean up on unmount
    return () => {
      socket.off("vendor-added", handleVendor);
      socket.off("new-vendor-partner", handleVendor);
      socket.off("new-installation-partner", handleIP);
      socket.off("new-client-partner", handleClient);
    };
  };

  useEffect(() => {
    fetchDashboardSummary();
    fetchOngoingCampaigns();
    fetchComparedCounts();
    const cleanupSocket = setupSocketListeners();

    return () => cleanupSocket();
  }, []);

  return (
    <div style={{ overflowY: "auto", height: "92vh" }}>
      {!loading ? (
        <Grid container p={2}>
          <Grid item xs={12}>
            <TopCards
              totalCampaigns={totalCampaigns}
              totalOrganizationDetail={totalOrganizationDetail}
              TotalEarning={TotalEarning}
              TotalIps={totalIps}
              totalVendors={totalVendors}
              comparedCounts={comparedCounts}
            />
          </Grid>
          <Grid container spacing={3}>
            <Grid item sm={12} md={4} lg={4}>
              <Clients lastMonthData={lastMonthData} />
            </Grid>
            <Grid item sm={12} md={4} lg={4}>
              <Vendors lastMonthData={lastMonthData} />
            </Grid>
            <Grid item sm={12} md={4} lg={4}>
              <Ips lastMonthData={lastMonthData} />
            </Grid>
            <Grid item sm={12} md={8} lg={8}>
              <CampaignTable
                campaignList={campaignList}
                isSites={isSites}
                productAreaSum={productAreaSum}
                ongoingLoading={ongoingLoading}
                initialLoading={initialCampaignLoading}
                totalCampaigns={totalCampaigns}
                fetchMore={() => fetchOngoingCampaigns(page, 10)}
                hasMore={hasMore}
              />
            </Grid>
            <Grid item sm={12} md={4} lg={4}>
              <AdminList
                kamdata={kamdata}
                initialLoading={initialCampaignLoading}
              />
            </Grid>
            {/* <Grid item xs={12} sm={12} md={12} lg={4}>
        <Analytics />
        </Grid> */}
          </Grid>
        </Grid>
      ) : (
        <div className=" flex justify-center items-center h-full">
          <Box sx={{ display: "flex" }}>
            <CircularProgress />
          </Box>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
